
import {lazy, Suspense} from 'react';
import {routesPath} from './routes';
import {
  ClinicalTrialGeneralInformationPage
} from '../pages/ClinicalTrial/ClinicalTrialGeneralInformation/ClinicalTrialGeneralInformation';
import {
  ClinicalTrialImagingLabelsPage
} from '../pages/ClinicalTrial/ClinicalTrialImagingLabels/ClinicalTrialImagingLabels';
import {ClinicalTrialOverviewPage} from '../pages/ClinicalTrial/ClinicalTrialOverview/ClinicalTrialOverview';
import {ClinicalTrialVisitsPage} from '../pages/ClinicalTrial/ClinicalTrialVisits/ClinicalTrialVisits';
import {ClinicalTrialSitesPage} from '../pages/ClinicalTrial/ClinicalTrialSites/ClinicalTrialSites';
import {AuditTrialsList} from 'src/pages/AuditTrial/AuditTrialsList';
import {Layout} from '@components/Layout/Layout';
import {Navigate, Outlet} from 'react-router-dom';
import {ClinicalTrial} from '../pages/ClinicalTrial/ClinicalTrial/ClinicalTrial';
import {ClinicalTrialListPage} from '../pages/ClinicalTrial/ClinicalTrialList/ClinicalTrialList';
import {UserRoles} from '@dynamika-shared/types/auth';
import {WithUserRolesHoc} from '@components/HOC/WithUserRoles/WithUserRoles.hoc';
import {ClinicalTrialReports} from 'src/pages/ClinicalTrial/ClinicalTrialReports/ClinicalTrialReports';
import {
  TaskDefinitionForm
} from 'src/pages/ClinicalTrial/Readings/TaskDefinition/TaskDefinitionForm/TaskDefinitionForm';
import {ReadingsClinicalTrial} from 'src/pages/ClinicalTrial/Readings/ReadingsClinicalTrial';
import {readingsSubRoutes} from '@router/Readings.routes';
import {ClinicalTrialForms} from 'src/pages/ClinicalTrial/ClinicalTrialForms/ClinicalTrialForms';
import {
  ClinicalTrialFormsAssigned
} from 'src/pages/ClinicalTrial/ClinicalTrialForms/ClinicalTrialFormsAssigned/ClinicalTrialFormsAssigned';
import {
  ClinicalTrialFormsManagement
} from 'src/pages/ClinicalTrial/ClinicalTrialForms/ClinicalTrialFormsManagement/ClinicalTrialFormsManagement';
import {ClinicalTrialNotifications} from 'src/pages/ClinicalTrial/ClinicalTrialNotifications/ClinicalTrialNotifications';
import {
  ClinicalTrialNotificationsConfiguration
} from 'src/pages/ClinicalTrial/ClinicalTrialNotifications/ClinicalTrialNotificationsConfiguration/ClinicalTrialNotificationsConfiguration';
import {
  ClinicalTrialNotificationsHistory
} from 'src/pages/ClinicalTrial/ClinicalTrialNotifications/ClinicalTrialNotificationsHistory/ClinicalTrialNotificationsHistory';
import {
  ClinicalTrialMembersPageView
} from 'src/pages/ClinicalTrial/ClinicalTrialTeam/components/Members/ClinicalTrialMembers.view';
import {ClinicalTrialMembersPage} from 'src/pages/ClinicalTrial/ClinicalTrialTeam/ClinicalTrialTeamMembersPage';
import {PageLoading} from '@dynamika-shared/ui/PageLoading/PageLoading';
import {SupportSubjectList} from 'src/pages/ClinicalTrial/SupportSubjectList/SupportSubjectList';
import {SupportSubjectDetail} from 'src/pages/ClinicalTrial/SupportSubjectList/SupportSubjectDetail';
import { getAllFeatureFlags } from '@dynamika-shared/services/featureFlags';
import {
  ClinicalTrialNotificationsSiteRecipients
} from 'src/pages/ClinicalTrial/ClinicalTrialNotifications/ClinicalTrialNotificationsSiteRecipients/ClinicalTrialNotificationsSiteRecipients';

const SponsorOverview = lazy(
  () => import('src/pages/Sponsor/SponsorOverview/SponsorOverview')
);
const SponsorEligibility = lazy(
  () => import('src/pages/Sponsor/SponsorEligibility/SponsorEligibility')
);
const SponsorStatistics = lazy(
  () => import('src/pages/Sponsor/SponsorStatistics/SponsorStatistics')
);
const ReadingCompleted = lazy(
  () => import('src/pages/Sponsor/ReadingCompleted/ReadingCompleted')
);
const ReaderPlainForm = lazy(
  () => import('src/pages/Readings/ReadingTaskPage/ReaderPlainForm')
);

const ReadingTaskViewerPage = lazy(
  () => import('src/pages/Readings/ReadingTaskPage/ReadingTaskViewerPage')
);
const ReadingTaskViewerPageDetached = lazy(
  () =>
    import('src/pages/Readings/ReadingTaskPage/ReadingTaskViewerPageDetached')
);
const OverviewUploadsView = lazy(
  () => import('src/pages/OverviewUploadsView/OverviewUploadsView')
);

const OverviewQueries = lazy(
  () => import('src/pages/OverviewQueries/OverviewQueries')
);

const OverviewQueryPage = lazy(
  () => import('src/pages/OverviewQueries/OverviewQueryPage')
);

const QCViewer = lazy(
  () => import('src/pages/QC/QCVisit/QCViewer')
);

const SimpleViewer = lazy(() => import('src/pages/SimpleViewer/SimpleViewer'));

const featureFlags = getAllFeatureFlags();


export const clinicalTrialDashboardTabsRoutes = [
  {
    title: 'Overview',
    path: routesPath.clinicalTrialOverviewDashboard.split('/').pop() ?? '',
    element: (
      <Suspense fallback={<PageLoading className='h-full' />}>
        <SponsorOverview />
      </Suspense>
    ),
  },
  {
    title: 'Eligibility',
    path: routesPath.clinicalTrialOverviewEligibility.split('/').pop() ?? '',
    element: (
      <Suspense fallback={<PageLoading className='h-full' />}>
        <SponsorEligibility />
      </Suspense>
    ),
  },
  {
    title: 'Statistics',
    path: routesPath.clinicalTrialOverviewStatistics.split('/').pop() ?? '',
    element: (
      <Suspense fallback={<PageLoading className='h-full' />}>
        <SponsorStatistics />
      </Suspense>
    ),
  },
  {
    title: 'Readings Completed',
    path: routesPath.clinicalTrialOverviewReadingsCompleted
      .split('/')
      .pop()!,
    element: (
      // ClinicalTrialOverviewPage owns the full tab-aware trail here; suppress
      // this lazy child's own crumb so it cannot overwrite it on chunk load.
      <Suspense fallback={<PageLoading className='h-full' />}>
        <ReadingCompleted manageBreadcrumb={false} />
      </Suspense>
    ),
  },
  {
    title: 'Uploads',
    path: routesPath.clinicalTrialOverviewUploads.split('/').pop()!,
    element: (
      // ClinicalTrialOverviewPage owns the full tab-aware trail here; suppress
      // this lazy child's own crumb so it cannot overwrite it on chunk load.
      <Suspense fallback={<PageLoading className='h-full' />}>
        <OverviewUploadsView manageBreadcrumb={false} />
      </Suspense>
    ),
  },
  {
    title: 'Queries',
    path: routesPath.clinicalTrialOverviewQueries.split('/').pop() ?? '',
    navAllowedRoles: [UserRoles.PROJECT_MANAGER],
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.PROJECT_MANAGER]}>
        <Suspense fallback={<PageLoading className='h-full' />}>
          <OverviewQueries />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    title: 'Audit Trails',
    path: routesPath.clinicalTrialOverviewAuditTrails.split('/').pop() ?? '',
    element: <AuditTrialsList />,
  },
];

export const clinicalTrialOverviewRoutes = [
  {
    title: 'Dashboard',
    key: 'dashboard',
    path: routesPath.clinicalTrialOverview,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
    element: <ClinicalTrialOverviewPage />,
    children: [
      {
        path: '',
        element: (
          <Navigate
            to={routesPath.clinicalTrialOverviewDashboard.split('/').pop()!}
            replace
          />
        ),
      },
      ...clinicalTrialDashboardTabsRoutes,
      // NOTE: kept out of `clinicalTrialDashboardTabsRoutes` on purpose — that
      // array also drives the tab strip, and a detail route listed there would
      // render as an extra tab.
      {
        path: `${routesPath.clinicalTrialOverviewQueries
          .split('/')
          .pop()}/:queryId`,
        navAllowedRoles: [UserRoles.PROJECT_MANAGER],
        element: (
          <WithUserRolesHoc allowedRoles={[UserRoles.PROJECT_MANAGER]}>
            <Suspense fallback={<PageLoading className='h-full' />}>
              <OverviewQueryPage />
            </Suspense>
          </WithUserRolesHoc>
        ),
      },
    ],
  },
];

export const clinicalTrialFormsRoutes = [
  {
    title: 'Assigned',
    path: 'assigned',
    element: <ClinicalTrialFormsAssigned />,
  },
  {
    title: 'Manage Forms',
    path: 'management',
    element: <ClinicalTrialFormsManagement />,
  },
];

export const clinicalTrialNotificationsTabsRoutes = [
  {
    title: 'Configuration',
    path:
      routesPath.clinicalTrialNotificationsConfiguration.split('/').pop() ?? '',
    element: <ClinicalTrialNotificationsConfiguration />,
  },
  {
    title: 'History',
    path: routesPath.clinicalTrialNotificationsHistory.split('/').pop() ?? '',
    element: <ClinicalTrialNotificationsHistory />,
  },
  {
    title: 'Site Recipients',
    path:
      routesPath.clinicalTrialNotificationsSiteRecipients.split('/').pop() ?? '',
    element: <ClinicalTrialNotificationsSiteRecipients />,
  },
];

export const clinicalTrialTeamMembersRoutes = [
  {
    title: 'Members',
    path: 'members',
    element: <ClinicalTrialMembersPageView />,
  },
];

export const clinicalTrialSubRoutes = [
  ...clinicalTrialOverviewRoutes,
  {
    title: 'Subject List',
    path: routesPath.clinicalTrialSubjectList,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SUPPORT]}>
        <SupportSubjectList />
      </WithUserRolesHoc>
    ),
    navAllowedRoles: [UserRoles.SUPPORT],
    key: 'subjectList',
  },
  {
    title: 'General',
    path: routesPath.clinicalTrialGeneralInformation,
    element: <ClinicalTrialGeneralInformationPage />,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
    key: 'generalInfo',
  },
  {
    title: 'Imaging Labels',
    path: routesPath.clinicalTrialImagingLabels,
    element: <ClinicalTrialImagingLabelsPage />,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
    key: 'imagingLabels',
  },
  {
    title: 'Visits Schedule',
    path: routesPath.clinicalTrialVisits,
    element: <ClinicalTrialVisitsPage />,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
    key: 'visits',
  },
  {
    title: 'Forms',
    path: routesPath.clinicalTrialForms,
    element: <ClinicalTrialForms />,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
    key: 'forms',
    children: [
      {
        path: '',
        element: <Navigate to={'assigned'} replace />,
      },
      ...clinicalTrialFormsRoutes,
    ],
  },
  ...(featureFlags['SHOW_CT_TASKS_DEFINITION_TAB_FLAG']
    ? [
        {
          title: 'Reading Config',
          path: routesPath.readings,
          element: <ReadingsClinicalTrial />,
          navAllowedRoles: [
            UserRoles.USER,
            UserRoles.ADMIN,
            UserRoles.PROJECT_MANAGER,
          ],
          children: [
            {
              path: '',
              element: (
                <Navigate
                  to={routesPath.taskDefinition.split('/').pop() as string}
                  replace
                />
              ),
            },
            ...readingsSubRoutes,
          ],
        },
      ]
    : []),
  {
    title: 'Sites',
    path: routesPath.clinicalTrialSites,
    element: <ClinicalTrialSitesPage />,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
  },
  {
    title: 'Team',
    path: routesPath.clinicalTrialTeam,
    element: <ClinicalTrialMembersPage />,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
    children: [
      {
        path: '',
        element: <Navigate to={'members'} replace />,
      },
      ...clinicalTrialTeamMembersRoutes,
    ],
  },
  {
    title: 'Reports',
    path: routesPath.clinicalTrialReports,
    element: <ClinicalTrialReports />,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
  },
  {
    title: 'Notifications',
    path: routesPath.clinicalTrialNotifications,
    element: <ClinicalTrialNotifications />,
    navAllowedRoles: [
      UserRoles.PROJECT_MANAGER,
    ],
    children: [
      {
        path: '',
        element: (
          <Navigate
            to={routesPath.clinicalTrialNotificationsConfiguration
              .split('/')
              .pop()!}
            replace
          />
        ),
      },
      ...clinicalTrialNotificationsTabsRoutes,
    ],
  },
];

export const clinicalTrialRoutes = [
  {
    path: routesPath.clinicalTrials,
    element: (
      <WithUserRolesHoc
        allowedRoles={[
          UserRoles.USER,
          UserRoles.ADMIN,
          UserRoles.PROJECT_MANAGER,
          UserRoles.SUPPORT,
        ]}
      >
        <Layout>
          <ClinicalTrialListPage />
        </Layout>
      </WithUserRolesHoc>
    ),
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
      UserRoles.SUPPORT,
    ],
  },
  {
    path: routesPath.taskDefinitionForm,
    element: (
      <WithUserRolesHoc
        allowedRoles={[
          UserRoles.USER,
          UserRoles.ADMIN,
          UserRoles.PROJECT_MANAGER,
        ]}
      >
        <Layout>
          <TaskDefinitionForm />
        </Layout>
      </WithUserRolesHoc>
    ),
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
    children: [],
  },
  {
    path: routesPath.clinicalTrial,
    element: (
      <WithUserRolesHoc
        allowedRoles={[
          UserRoles.USER,
          UserRoles.ADMIN,
          UserRoles.PROJECT_MANAGER,
          UserRoles.SUPPORT,
        ]}
      >
        <Layout>
          <ClinicalTrial>
            <Outlet />
          </ClinicalTrial>
        </Layout>
      </WithUserRolesHoc>
    ),
    children: clinicalTrialSubRoutes,
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
      UserRoles.SUPPORT,
    ],
  },
  {
    path: routesPath.clinicalTrialSubjectDetail,
    element: (
      <WithUserRolesHoc allowedRoles={[UserRoles.SUPPORT]}>
        <Layout>
          <SupportSubjectDetail />
        </Layout>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.clinicalTrialTaskViewer,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPage />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.clinicalTrialTaskSimpleViewer,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <SimpleViewer />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.clinicalTrialQcTaskViewer,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN, UserRoles.SPONSOR]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <QCViewer />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.clinicalTrialSubTaskViewer,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPage />
        </Suspense>
      </WithUserRolesHoc>
    ),
  },
  {
    path: routesPath.clinicalTrialTaskPlain,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReaderPlainForm />
        </Suspense>
      </WithUserRolesHoc>
    ),
    navAllowedRoles: [
      UserRoles.USER,
      UserRoles.ADMIN,
      UserRoles.PROJECT_MANAGER,
    ],
  },
  {
    path: routesPath.clinicalTrialTaskViewerDetached,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPageDetached />
        </Suspense>
      </WithUserRolesHoc>
    ),
    navAllowedRoles: [UserRoles.ADMIN, UserRoles.PROJECT_MANAGER],
  },
  {
    path: routesPath.clinicalTrialSubTaskViewerDetached,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPageDetached />
        </Suspense>
      </WithUserRolesHoc>
    ),
    navAllowedRoles: [UserRoles.ADMIN, UserRoles.PROJECT_MANAGER],
  },
  {
    path: routesPath.clinicalTrialSubTaskViewerDetached,
    element: (
      <WithUserRolesHoc
        allowedRoles={[UserRoles.PROJECT_MANAGER, UserRoles.ADMIN]}
      >
        <Suspense fallback={<PageLoading className='h-full' />}>
          <ReadingTaskViewerPageDetached />
        </Suspense>
      </WithUserRolesHoc>
    ),
    navAllowedRoles: [UserRoles.ADMIN, UserRoles.PROJECT_MANAGER],
  },
];
